/**
 * The AI wallet in Settings (TMMAPP-0113 §C 8; undrawn, built from the prototype's settings-card
 * patterns): a section of the Billing card. Available (`figureSm`) with Reserved and Used this
 * period, the personal limit, Auto-recharge (`PATCH /v1/me/ai-wallet`), a failed last recharge as a
 * warning banner with UPDATE CARD, TOP UP (or ENABLE AI before the first purchase), and the history,
 * newest first, paged by cursor. The meter is `GET /v1/me/billing`'s `aiWallet` — no second read.
 * TMMAPP-0114 (TMMAPI-0152): a debt above zero is a `warningSoft` line, a reached recharge limit a
 * line under the switch, and ENABLE AI shows "{amount} plus applicable tax" (TMMAPP-0119 item 9).
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { aiWalletApi, type AiWallet, type AiWalletEntry } from '@/api/aiWallet';
import { MoneyText } from '@/components/MoneyText';
import { storedLimit } from '@/features/ai/aiLimitPatch';
import { SubmitButton } from '@/components/SubmitButton';
import { featureLabel } from '@/features/payees/aiFeatureLabel';
import { settingsHref, type SettingsNotify } from '@/features/settings/settingsHref';
import { formatInstantStamp, formatMoneyMinor } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useMe } from '@/session/meHooks';
import { resolveTokens } from '@/theme/theme';
import { ChargeOutcomeNotice } from './ChargeOutcomeNotice';
import { chargePreview } from './chargePreview';
import { TopUpDialog } from './TopUpDialog';
import { chargeInFlight, declineKey, topUpStaysOpen, useAiWalletCharge } from './useAiWalletCharge';

interface AiWalletSectionProps {
  wallet: AiWallet;
  zone: string;
  onNotify: SettingsNotify;
}

/** USAGE with a feature stays "Usage · {feature}". An unknown kind is the generic label. */
function entryKindLabel(t: TFunction, entry: AiWalletEntry): string {
  if (entry.kind === 'OTHER') return t('aiWallet.kindOther');
  if (entry.kind === 'USAGE' && entry.failed) {
    return entry.feature !== null
      ? t('aiWallet.kindUsageFailed', { feature: featureLabel(t, entry.feature) })
      : t('aiWallet.kindFailedCharged');
  }
  if (entry.kind === 'USAGE' && entry.feature !== null) {
    return t('aiWallet.kindUsage', { feature: featureLabel(t, entry.feature) });
  }
  return t(`aiWallet.kind.${entry.kind}`);
}

/** A funding entry with served tax reads "{amount} ({total} charged, incl. {tax} tax)". */
function EntryAmount({ entry, currency }: { entry: AiWalletEntry; currency: string }) {
  const taxMinor = entry.taxMinor;
  const totalMinor = entry.totalMinor;
  if (
    (entry.kind === 'FIRST_PURCHASE' || entry.kind === 'TOP_UP' || entry.kind === 'RECHARGE') &&
    taxMinor !== null &&
    totalMinor !== null
  ) {
    return (
      <Trans
        i18nKey="aiWallet.entryTaxed"
        components={{
          amount: <MoneyText minor={entry.amountMinor} currency={currency} variant="body2" />,
          tax: <MoneyText minor={taxMinor} currency={currency} variant="body2" />,
          total: <MoneyText minor={totalMinor} currency={currency} variant="body2" />,
        }}
      />
    );
  }
  return (
    <MoneyText minor={entry.amountMinor} currency={currency} variant="body2" tone="signed" signed />
  );
}

const LIMIT_KEYS = {
  PLATFORM: 'aiWallet.personalLimitPlatform',
  PERSON: 'aiWallet.personalLimitPerson',
  UNKNOWN: 'aiWallet.personalLimitNeutral',
} as const;

/**
 * Whose limit `personCapMinor` is (TMMAPP-0118 C10): the served `personCapSource`. For a source this
 * build doesn't know, the plan's maximum when the cap equals the served `maxAiSpendMinor` and the
 * person set no limit of their own; otherwise it can't be told, and reads a neutral "AI spending
 * limit", never "Your limit".
 */
function capSource(
  wallet: Pick<AiWallet, 'personCapSource' | 'personCapMinor' | 'maxAiSpendMinor'>,
  ownLimitMinor: number | null,
): keyof typeof LIMIT_KEYS {
  if (wallet.personCapSource !== null) return wallet.personCapSource;
  if (ownLimitMinor === null && wallet.personCapMinor === wallet.maxAiSpendMinor) return 'PLATFORM';
  return 'UNKNOWN';
}

/** The audit's timestamp shape, `YYYY-MM-DD HH:mm` in the viewer's zone, set in mono; as received if unreadable. */
function stamp(at: string, zone: string): string {
  try {
    return formatInstantStamp(at, zone);
  } catch {
    return at;
  }
}

export function AiWalletSection({ wallet: served, zone, onNotify }: AiWalletSectionProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const me = useMe();
  // The billing read's meter until this section re-reads it (a charge, the switch).
  const [fresh, setFresh] = useState<AiWallet | null>(null);
  const wallet = fresh ?? served;
  const [switchBusy, setSwitchBusy] = useState(false);
  const [topUpOpen, setTopUpOpen] = useState(false);
  const [entries, setEntries] = useState<AiWalletEntry[]>([]);
  const [cursor, setCursor] = useState<number | null>(null);
  const [historyStatus, setHistoryStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [moreBusy, setMoreBusy] = useState(false);

  const loadHistory = useCallback(async () => {
    try {
      const page = await aiWalletApi.entries();
      setEntries(page.entries);
      setCursor(page.nextCursor);
      setHistoryStatus('ready');
    } catch {
      setHistoryStatus('error');
    }
  }, []);

  const onWallet = useCallback(
    (next: AiWallet | null) => {
      if (next === null) return;
      setFresh(next);
      void loadHistory();
    },
    [loadHistory],
  );
  const charge = useAiWalletCharge({ wallet, onWallet });
  const { outcome } = charge;

  useEffect(() => {
    queueMicrotask(() => {
      void loadHistory();
    });
  }, [loadHistory]);

  async function loadMore() {
    if (cursor === null || moreBusy) return;
    setMoreBusy(true);
    try {
      const page = await aiWalletApi.entries(cursor);
      setEntries((rows) => [...rows, ...page.entries]);
      setCursor(page.nextCursor);
    } catch {
      onNotify(t('aiWallet.errorGeneric'), 'error');
    } finally {
      setMoreBusy(false);
    }
  }

  async function handleAutoRecharge(next: boolean) {
    if (switchBusy) return;
    setSwitchBusy(true);
    try {
      const updated = await aiWalletApi.setAutoRecharge(next);
      setFresh(updated);
      onNotify(t(next ? 'aiWallet.autoRechargeOnSaved' : 'aiWallet.autoRechargeOffSaved'));
    } catch {
      onNotify(t('aiWallet.errorGeneric'), 'error');
    } finally {
      setSwitchBusy(false);
    }
  }

  const money = (minor: number) => formatMoneyMinor(minor, wallet.currency, locale);
  const label = { fontSize: 12, color: tokens.textLabel('surface') } as const;
  const failed = wallet.lastRecharge?.status === 'FAILED' ? wallet.lastRecharge : null;
  const busy = outcome.kind === 'busy';
  const inFlight = chargeInFlight(outcome);
  const enablePreview = wallet.firstPurchaseDone
    ? null
    : chargePreview(t, wallet.firstPurchaseMinor, wallet.currency, locale);

  return (
    <Box data-ai-wallet-section="">
      <Typography variant="h3" gutterBottom>
        {t('aiWallet.title')}
      </Typography>
      <Stack spacing={2}>
        {failed !== null && (
          <Alert
            severity="warning"
            action={
              <Button
                component={RouterLink}
                to={settingsHref('subscription')}
                color="inherit"
                size="small"
              >
                {t('aiWallet.updateCard')}
              </Button>
            }
          >
            {t('aiWallet.rechargeFailed', { reason: t(declineKey(failed.failureCode)) })}
          </Alert>
        )}
        {wallet.debtMinor > 0 && (
          <Typography
            variant="body2"
            data-ai-debt=""
            sx={{
              bgcolor: tokens.warningSoft,
              color: tokens.warningText,
              borderRadius: `${tokens.radius.control}px`,
              p: '8px 12px',
            }}
          >
            {t('aiWallet.debt', { debt: money(wallet.debtMinor) })}
          </Typography>
        )}

        {wallet.firstPurchaseDone ? (
          <Box>
            <Typography sx={label}>{t('aiWallet.available')}</Typography>
            {/* The figure sits under its label (start-aligned), as the stat cards do. */}
            <Box sx={{ '& .MuiTypography-root': { textAlign: 'start' } }}>
              <MoneyText
                minor={wallet.availableMinor}
                currency={wallet.currency}
                variant="figureSm"
                component="div"
              />
            </Box>
            <Typography sx={label}>
              {t('aiWallet.reservedUsed', {
                reserved: money(wallet.reservedMinor),
                used: money(wallet.usedThisPeriodMinor),
              })}
            </Typography>
            {wallet.personCapMinor !== null && wallet.personCapMinor > 0 && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {/* Whose limit applies (MM-D-134): the plan's maximum, the person's own, or —
                    when neither the source nor the figures tell — a neutral limit. */}
                {t(LIMIT_KEYS[capSource(wallet, storedLimit(me.user.aiMonthlyCapMinor))], {
                  cap: money(wallet.personCapMinor),
                  remaining: money(wallet.personCapRemainingMinor ?? 0),
                })}
              </Typography>
            )}
          </Box>
        ) : (
          <Box>
            <Typography variant="body2">
              {t('aiWallet.prompt.firstPurchase', { amount: money(wallet.firstPurchaseMinor) })}
            </Typography>
            {enablePreview !== null && (
              <Typography variant="body2" data-charge-preview="">
                {enablePreview}
              </Typography>
            )}
          </Box>
        )}

        <Box>
          {wallet.firstPurchaseDone ? (
            <Button
              variant="outlined"
              disabled={inFlight}
              onClick={() => {
                charge.reset();
                setTopUpOpen(true);
              }}
            >
              {t('aiWallet.topUp')}
            </Button>
          ) : (
            <SubmitButton
              loading={busy}
              disabled={inFlight && !busy}
              onClick={() => void charge.purchase()}
            >
              {t('aiWallet.enable')}
            </SubmitButton>
          )}
        </Box>
        {(!topUpOpen || !topUpStaysOpen(outcome)) && (
          <ChargeOutcomeNotice outcome={outcome} onRefresh={() => void charge.refresh()} />
        )}

        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={wallet.autoRecharge}
                disabled={switchBusy}
                onChange={(event) => void handleAutoRecharge(event.target.checked)}
                slotProps={{ input: { 'aria-busy': switchBusy || undefined } }}
              />
            }
            label={t('aiWallet.autoRecharge')}
          />
          <Typography sx={label} data-auto-recharge-cap="">
            {t('aiWallet.autoRechargeUpTo', { count: wallet.autoRechargeLimit })}
          </Typography>
          {wallet.autoRechargeLimitReached && (
            <Typography sx={label} data-auto-recharge-limit="">
              {t('aiWallet.autoRechargeLimit')}
            </Typography>
          )}
          <Typography sx={label}>
            {t('aiWallet.autoRechargeNote', {
              trigger: money(wallet.triggerMinor),
              recharge: money(wallet.rechargeAmountMinor),
            })}
          </Typography>
          {wallet.monthlyCreditMinor > 0 && (
            <Typography sx={label}>
              {t('aiWallet.monthlyCredit', { amount: money(wallet.monthlyCreditMinor) })}
            </Typography>
          )}
        </Box>

        <Box>
          <Typography variant="subtitle2" component="h4" gutterBottom>
            {t('aiWallet.history')}
          </Typography>
          {historyStatus === 'loading' && (
            <Typography variant="body2">{t('common.loading')}</Typography>
          )}
          {historyStatus === 'error' && (
            <Alert
              severity="warning"
              action={
                <Button color="inherit" size="small" onClick={() => void loadHistory()}>
                  {t('common.retry')}
                </Button>
              }
            >
              {t('aiWallet.historyError')}
            </Alert>
          )}
          {historyStatus === 'ready' && entries.length === 0 && (
            <Typography variant="body2">{t('aiWallet.historyEmpty')}</Typography>
          )}
          {historyStatus === 'ready' && entries.length > 0 && (
            <Table size="small" aria-label={t('aiWallet.history')}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('aiWallet.colDate')}</TableCell>
                  <TableCell>{t('aiWallet.colKind')}</TableCell>
                  <TableCell align="right">{t('aiWallet.colAmount')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell>
                      {/* The date and the time each hold together; at 390 the time drops below. */}
                      <Typography
                        variant="mono"
                        sx={{ display: 'inline-flex', flexWrap: 'wrap', columnGap: 1 }}
                      >
                        {stamp(entry.at, zone)
                          .split(' ')
                          .map((part) => (
                            <Box key={part} component="span" sx={{ whiteSpace: 'nowrap' }}>
                              {part}
                            </Box>
                          ))}
                      </Typography>
                    </TableCell>
                    <TableCell>{entryKindLabel(t, entry)}</TableCell>
                    <TableCell align="right">
                      <EntryAmount entry={entry} currency={wallet.currency} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {historyStatus === 'ready' && cursor !== null && (
            <Button sx={{ mt: 1 }} onClick={() => void loadMore()} loading={moreBusy}>
              {t('aiWallet.loadMore')}
            </Button>
          )}
        </Box>
      </Stack>

      {topUpOpen && topUpStaysOpen(outcome) && (
        <TopUpDialog
          currency={wallet.currency}
          minimumMinor={wallet.rechargeAmountMinor}
          outcome={outcome}
          onTopUp={(amountMinor) => void charge.topUp(amountMinor)}
          onRefresh={() => void charge.refresh()}
          onClose={() => {
            setTopUpOpen(false);
            charge.reset();
          }}
        />
      )}
    </Box>
  );
}
